import { useCallback, useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { Card, Notice } from './Shell';
import { TextField } from './Field';
import { Stamp } from './Stamp';
import { CopyLink } from './CopyLink';

interface CustomDomain {
  domain: string;
  status: 'pending_dns' | 'validating' | 'active' | 'failed';
  routingEndpoint: string;
  requestedAt: string;
  verifiedAt?: string;
  lastError?: string;
}

const STAMP: Record<CustomDomain['status'], { label: string; tone: 'ok' | 'pending' | 'flag' }> = {
  pending_dns: { label: 'Waiting on DNS', tone: 'pending' },
  validating: { label: 'Issuing certificate', tone: 'pending' },
  active: { label: 'Live', tone: 'ok' },
  failed: { label: 'Failed', tone: 'flag' },
};

/** What the sitter should do next, which is not the same as the status. */
const NEXT_STEP: Record<CustomDomain['status'], string> = {
  pending_dns: 'Add the record below. We will keep checking for it.',
  validating:
    'The record is in place and the certificate is being issued. This usually takes a few minutes and finishes on its own.',
  active: '',
  failed: '',
};

/**
 * Neither step of the handover is instant and neither raises an event: the DNS
 * record lands when the sitter's provider gets round to it, and CloudFront
 * redeploys for a minute or two after the certificate is attached. The check
 * is what moves the claim forward, so leaving it on a button meant the sitter
 * had to guess when to press it, and pressing it early looked broken.
 */
const POLL_MS = 15_000;

/**
 * A sitter's own hostname. The certificate is issued and renewed for them, so
 * the only thing asked of them is one CNAME, and the only question this screen
 * has to answer is whether it has taken effect yet.
 */
export function CustomDomainCard({ tenantId }: { tenantId: string }) {
  const [domain, setDomain] = useState<CustomDomain | null>(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A failed read is not the same as no claim. Without this the card falls back
  // to the empty form and the sitter loses sight of the domain they already
  // added, along with the button that would check it again.
  const [unreadable, setUnreadable] = useState(false);
  const [checking, setChecking] = useState(false);

  const describe = (err: unknown) =>
    err instanceof ApiError ? err.message : 'Something went wrong. Try again.';

  const refresh = useCallback(async () => {
    try {
      const data = await api<{ customDomain: CustomDomain | null }>(`/tenants/${tenantId}/domain`);
      setDomain(data.customDomain);
      setError(null);
      setUnreadable(false);
    } catch (err) {
      setError(describe(err));
      setUnreadable(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const settling = domain?.status === 'pending_dns' || domain?.status === 'validating';
  useEffect(() => {
    if (!settling) return;
    const timer = setInterval(() => void refresh(), POLL_MS);
    return () => clearInterval(timer);
  }, [settling, refresh]);

  const claim = (event: React.FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const data = await api<{ customDomain: CustomDomain }>(`/tenants/${tenantId}/domain`, {
          method: 'PUT',
          body: { domain: input.trim() },
        });
        setDomain(data.customDomain);
        setInput('');
      } catch (err) {
        setError(describe(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  const release = () => {
    setBusy(true);
    setError(null);
    void (async () => {
      try {
        await api(`/tenants/${tenantId}/domain`, { method: 'DELETE' });
        setDomain(null);
      } catch (err) {
        setError(describe(err));
      } finally {
        setBusy(false);
      }
    })();
  };

  if (loading) return null;

  return (
    <Card tab="Your own domain" className="mb-6 p-5">
      {error && (
        <div className="mb-4">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {unreadable && !domain ? (
        <div className="space-y-4">
          <p className="text-sm text-[color:var(--color-ink-soft)]">
            We could not read the state of your domain just now. Nothing has changed, and your
            address on the platform is unaffected.
          </p>
          <button type="button" className="btn btn-secondary" onClick={() => void refresh()}>
            Try again
          </button>
        </div>
      ) : !domain ? (
        <form onSubmit={claim} className="space-y-4">
          <p className="text-sm text-[color:var(--color-ink-soft)]">
            Serve your booking page from an address you own, with your name on it and nothing
            else. The certificate is issued and renewed for you.
          </p>
          <TextField
            label="Domain"
            value={input}
            onChange={setInput}
            placeholder="book.yourbusiness.com"
            hint="A subdomain you are not already using, such as book. or hello."
            required
          />
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Setting up' : 'Use this domain'}
          </button>
        </form>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-sm">{domain.domain}</span>
            <Stamp tone={STAMP[domain.status].tone} flat>
              {STAMP[domain.status].label}
            </Stamp>
          </div>

          {domain.status === 'active' ? (
            <CopyLink url={`https://${domain.domain}`} label="Your booking page" />
          ) : (
            <>
              {/* The one thing the sitter has to do. Shown until it has taken
                  effect, because until then nothing else can happen. */}
              <div className="rounded border border-[color:var(--color-rule)] p-4">
                <p className="mb-3 text-sm text-[color:var(--color-ink-soft)]">
                  Add this record with whoever hosts your DNS, then check back. It is usually
                  minutes, but some providers take longer to publish a change.
                </p>
                <dl className="grid gap-3 sm:grid-cols-[auto_1fr] sm:gap-x-6">
                  <dt className="field-label">Type</dt>
                  <dd className="min-w-0 font-mono text-sm">CNAME</dd>
                  <dt className="field-label">Name</dt>
                  <dd className="min-w-0 break-all font-mono text-sm">{domain.domain}</dd>
                  <dt className="field-label">Value</dt>
                  <dd className="min-w-0">
                    <CopyLink url={domain.routingEndpoint} />
                  </dd>
                </dl>
              </div>
              <p className="text-sm text-[color:var(--color-ink-soft)]">
                {NEXT_STEP[domain.status]}
              </p>
              {domain.lastError && <Notice tone="info">{domain.lastError}</Notice>}
            </>
          )}

          <div className="flex flex-wrap gap-3">
            {domain.status !== 'active' && (
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setChecking(true);
                  void refresh().finally(() => setChecking(false));
                }}
                disabled={busy || checking}
              >
                {checking ? 'Checking' : 'Check again'}
              </button>
            )}
            <button type="button" className="btn btn-quiet" onClick={release} disabled={busy}>
              Remove
            </button>
          </div>

          <p className="text-sm text-[color:var(--color-ink-soft)]">
            Your address on the platform keeps working either way, so nothing breaks while this
            is being set up.
          </p>
        </div>
      )}
    </Card>
  );
}
